import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fmtLongDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const SVC_LABELS = {
  pipe_fence: 'Pipe Fence', custom_gate: 'Custom Gate', concrete: 'Concrete',
  metal_building: 'Metal Building', land_clearing: 'Land Clearing',
  dirt_work: 'Dirt/Gravel Work', gravel: 'Gravel Work',
};

const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function QuoteSign() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typedName, setTypedName] = useState('');
  const [title, setTitle] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/quote/${token}`)
      .then(r => { if (!r.ok) throw new Error('Quote not found'); return r.json(); })
      .then(data => { setQuote(data); if (data.signature) setSigned(true); })
      .catch(() => setError('This quote link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSign = async () => {
    if (!typedName.trim() || typedName.trim().length < 2) return;
    if (!agreed) return;
    setSigning(true);
    try {
      const res = await fetch(`${API}/api/quote/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typed_name: typedName.trim(), title: title.trim() }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.detail || 'Signing failed');
        setSigning(false);
        return;
      }
      setSigned(true);
    } catch {
      setError('Network error. Please try again.');
    }
    setSigning(false);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center', color: '#64748b' }}>
        <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#B91C1C', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        Loading quote...
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  );

  if (error && !quote) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>
          <i className="fa-solid fa-file-circle-xmark" style={{ color: '#B91C1C' }}></i>
        </div>
        <h2 style={{ fontSize: 18, color: '#1e293b', marginBottom: 8 }}>Quote Not Found</h2>
        <p style={{ color: '#64748b', fontSize: 14 }}>{error}</p>
      </div>
    </div>
  );

  // Expired quote — show professional expired state
  if (quote && quote.is_expired && !quote.signature) return (
    <div data-testid="quote-expired-view" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ maxWidth: 520, textAlign: 'center', background: '#fff', borderRadius: 12, padding: '48px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Quote Expired</h1>
        <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: '0 0 16px' }}>
          This quote ({quote.quote_number}) expired on {fmtLongDate(quote.valid_through)}.
        </p>
        <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: '0 0 24px' }}>
          Please contact North Texas Welding & Fabrication for an updated quote.
        </p>
        <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '16px 20px', textAlign: 'left' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Contact Us</div>
          <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.8 }}>
            <div>Phone: (940) 577-9440</div>
            <div>Email: info@ntxpipefence.com</div>
          </div>
        </div>
      </div>
    </div>
  );


  const q = quote;
  const ps = q.payment_schedule || {};
  const cust = q.customer_info || {};
  const calc = q.calculation || {};

  let validDateStr = '';
  try {
    validDateStr = fmtLongDate(q.valid_through);
  } catch { validDateStr = ''; }

  let createdDateStr = '';
  try {
    createdDateStr = fmtLongDate(q.created_at);
  } catch { createdDateStr = ''; }

  return (
    <div data-testid="quote-sign-page" style={{ minHeight: '100vh', background: '#f1f5f9', padding: '24px 16px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>NORTH TEXAS WELDING & FABRICATION, LLC</h1>
              <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>Welding & Fabrication | Pipe Fence | Metal Buildings | Concrete | Gates</p>
              <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Springtown, TX | (817) 771-7823</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', fontFamily: 'Georgia, serif' }}>QUOTE</div>
              <div style={{ fontSize: 12, color: '#475569' }}>{q.quote_number}</div>
              {createdDateStr && <div style={{ fontSize: 12, color: '#475569' }}>Date: {createdDateStr}</div>}
              {validDateStr && <div style={{ fontSize: 12, color: '#475569' }}>Valid Through: {validDateStr}</div>}
            </div>
          </div>
          <hr style={{ border: 'none', borderTop: '2px solid #B91C1C', margin: '16px 0' }} />

          {/* Customer & Project */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Prepared For</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{cust.name}</div>
              {cust.address && <div style={{ fontSize: 12, color: '#475569' }}>{[cust.address, cust.city, cust.state, cust.zip_code].filter(Boolean).join(', ')}</div>}
              {cust.phone && <div style={{ fontSize: 12, color: '#475569' }}>Phone: {cust.phone}</div>}
              {cust.email && <div style={{ fontSize: 12, color: '#475569' }}>Email: {cust.email}</div>}
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Project</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{q.project_title}</div>
              <div style={{ fontSize: 12, color: '#475569' }}>Services: {(q.services || []).map(s => SVC_LABELS[s] || s).join(', ')}</div>
            </div>
          </div>
        </div>

        {/* Pricing Table */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px 32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px', textTransform: 'uppercase' }}>Pricing</h2>
          {(q.line_items || []).length > 0 ? (
            <table data-testid="quote-pricing-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#64748b', fontWeight: 600 }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11, color: '#64748b', fontWeight: 600 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {q.line_items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '8px 10px', color: '#1e293b' }}>
                      <strong>{SVC_LABELS[item.service] || item.service}</strong>
                      {item.summary?.length > 0 && <span style={{ color: '#64748b' }}> — {item.summary.length} LF</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(item.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ color: '#64748b', fontSize: 13 }}>Detailed breakdown available in the PDF document.</div>
          )}

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '2px solid #B91C1C' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>QUOTED TOTAL</span>
            <span data-testid="quote-total" style={{ fontSize: 24, fontWeight: 700, color: '#B91C1C' }}>{fmt(q.quoted_price)}</span>
          </div>
          {calc.customer_range?.low && calc.customer_range?.high && (
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Estimated range: {fmt(calc.customer_range.low)} – {fmt(calc.customer_range.high)}. Final pricing subject to site conditions.
            </p>
          )}
        </div>

        {/* Payment Schedule */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px 32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 8px', textTransform: 'uppercase' }}>Payment Schedule</h2>
          <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
            Unless a different payment schedule is specifically stated in writing on the applicable estimate, proposal, contract, or invoice, the following standard payment schedule applies:
          </p>
          <div data-testid="quote-payment-schedule" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { pct: ps.deposit_percent || 50, label: 'Initial Deposit', amt: ps.deposit_amount || q.quoted_price * 0.5, terms: ps.deposit_terms || '' },
              { pct: ps.progress_percent || 30, label: 'Progress Payment', amt: ps.progress_amount || q.quoted_price * 0.3, terms: ps.progress_terms || '' },
              { pct: ps.final_percent || 20, label: 'Final Payment', amt: ps.final_amount || q.quoted_price * 0.2, terms: ps.final_terms || '' },
            ].map((tier, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 12px', background: i === 0 ? '#fef2f2' : '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{tier.pct}% — {tier.label}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{tier.terms}</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', marginLeft: 16 }}>{fmt(tier.amt)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {q.notes && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>Notes</h2>
            <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.5 }}>{q.notes}</p>
          </div>
        )}

        {/* Signature Section */}
        <div data-testid="quote-signature-section" style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
          {signed || q.signature ? (
            <div data-testid="quote-signed-confirmation">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40, color: '#16a34a', marginBottom: 8 }}>
                  <i className="fa-solid fa-circle-check"></i>
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#16a34a', margin: '0 0 8px' }}>Quote Accepted & Signed</h2>
                <div style={{ fontFamily: "'Georgia', 'Times New Roman', serif", fontSize: 28, fontStyle: 'italic', color: '#1e293b', margin: '16px 0 8px', padding: '8px 0', borderBottom: '1px solid #cbd5e1' }}>
                  {q.signature?.typed_name || typedName}
                </div>
                {(q.signature?.title || title) && (
                  <div style={{ fontSize: 13, color: '#475569' }}>{q.signature?.title || title}</div>
                )}
                <p style={{ fontSize: 11, color: '#64748b', marginTop: 12 }}>
                  This electronic signature is legally binding under the ESIGN Act and UETA.
                </p>
              </div>
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <a href={`${API}/api/quote/${token}/pdf`} target="_blank" rel="noopener noreferrer"
                   data-testid="download-signed-pdf-btn"
                   style={{ display: 'inline-block', padding: '10px 24px', background: '#1e293b', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: 6 }}></i> Download Signed PDF
                </a>
              </div>
            </div>
          ) : (
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Accept & Sign This Quote</h2>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px' }}>
                By typing your name below, you accept this estimate, the payment schedule, and the Invoice Declaration & Terms.
              </p>

              {error && <div style={{ background: '#fef2f2', color: '#B91C1C', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Full Name *</label>
                <input data-testid="esign-name-input" type="text" value={typedName} onChange={e => setTypedName(e.target.value)}
                  placeholder="Type your full legal name"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
              </div>

              {typedName.trim().length >= 2 && (
                <div style={{ margin: '12px 0 16px', padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>Signature Preview</div>
                  <div data-testid="esign-preview" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", fontSize: 28, fontStyle: 'italic', color: '#1e293b', borderBottom: '1px solid #94a3b8', paddingBottom: 4 }}>
                    {typedName}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Title / Position (optional)</label>
                <input data-testid="esign-title-input" type="text" value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Property Owner, Ranch Manager"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
              </div>

              <label data-testid="esign-agree-checkbox" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, accentColor: '#B91C1C' }} />
                <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                  I acknowledge and accept the quoted price of <strong>{fmt(q.quoted_price)}</strong>, the payment schedule, and the Invoice Declaration & Terms.
                  Payment of the initial deposit constitutes acceptance of this estimate.
                </span>
              </label>

              <button data-testid="esign-submit-btn" onClick={handleSign}
                disabled={signing || !agreed || typedName.trim().length < 2}
                style={{
                  width: '100%', padding: '12px 24px', background: (agreed && typedName.trim().length >= 2) ? '#B91C1C' : '#cbd5e1',
                  color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: (agreed && typedName.trim().length >= 2) ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s',
                }}>
                {signing ? 'Signing...' : 'Accept & Sign Quote'}
              </button>

              <p style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>
                Your IP address and timestamp will be recorded for legal verification.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '16px 0', color: '#94a3b8', fontSize: 11 }}>
          North Texas Welding and Fabrication, LLC | (817) 771-7823 | ntxpipefence.com
        </div>
      </div>
    </div>
  );
}
